import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { toast } from 'sonner';

const lines = (value) => asArray(value).join('\n');
const splitLines = (value) => String(value || '').split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
const money = (micros) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(micros || 0) / 1_000_000);
const count = (value) => new Intl.NumberFormat().format(Number(value || 0));

function proposalForm(app, growth) {
  return {
    campaign_name: `${app.name} Search`,
    destination_url: app.base_url,
    daily_budget: ((growth.daily_ad_budget_cents || 0) / 100).toFixed(2),
    keywords: lines(growth.approved_keywords),
    negative_keywords: lines(growth.negative_keywords),
    headlines: `${app.name}\nBuilt for your workflow\nGet started today`,
    descriptions: `Discover ${app.name} and see how it can simplify your workflow.\nVisit the official website to learn more and get started.`,
  };
}

export default function GoogleAdsControlPanel({ app, growth }) {
  const [status, setStatus] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [form, setForm] = useState(() => proposalForm(app, growth));
  const [managerId, setManagerId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusRes, proposalsRes] = await Promise.all([
        api.get(`/apps/${app.id}/growth/google-ads/status`),
        api.get(`/apps/${app.id}/growth/google-ads/proposals`),
      ]);
      setStatus(statusRes.data);
      setProposals(asArray(proposalsRes.data));
      setManagerId(statusRes.data.manager_customer_id || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load Google Ads controls');
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const saved = (growth.daily_ad_budget_cents || 0) / 100;
    if (saved > 0 && Number(form.daily_budget || 0) <= 0) {
      setForm((current) => ({ ...current, daily_budget: saved.toFixed(2) }));
    }
  }, [growth.daily_ad_budget_cents, form.daily_budget]);

  const metrics = status?.metrics || {};
  const selected = status?.selected_customer_id || '';
  const accountOptions = asArray(status?.available_customer_ids);
  const hardCap = (growth.daily_ad_budget_cents || 0) / 100;
  const proposalPayload = useMemo(() => ({
    campaign_name: form.campaign_name.trim(),
    destination_url: form.destination_url.trim(),
    daily_budget_cents: Math.round((Number(form.daily_budget) || 0) * 100),
    keywords: splitLines(form.keywords),
    negative_keywords: splitLines(form.negative_keywords),
    headlines: splitLines(form.headlines),
    descriptions: splitLines(form.descriptions),
  }), [form]);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/google-ads/connect`);
      window.location.assign(res.data.authorization_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the Google Ads connection');
      setBusy(false);
    }
  };

  const chooseAccount = async (customerId) => {
    setBusy(true);
    try {
      const res = await api.put(`/apps/${app.id}/growth/google-ads/account`, {
        customer_id: customerId,
        manager_customer_id: managerId.trim() || null,
      });
      setStatus(res.data);
      toast.success('Google Ads account assigned to this app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not select that Google Ads account');
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/google-ads/sync`);
      setStatus(res.data);
      toast.success('Google Ads results updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update Google Ads results');
    } finally {
      setBusy(false);
    }
  };

  const createProposal = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/google-ads/proposals`, proposalPayload);
      setProposals((current) => [res.data, ...current]);
      toast.success('Campaign proposal saved for approval — ads remain off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the campaign proposal');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (item, action) => {
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} “${item.campaign_name}”? This does not activate ads or spend money.`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/google-ads/proposals/${item.id}/${action}`, { note: null });
      setProposals((current) => current.map((candidate) => candidate.id === item.id ? res.data : candidate));
      toast.success(`${verb}d proposal — ads remain off`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action} proposal`);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect Google Ads from ${app.name}? Campaign proposals will be kept.`)) return;
    setBusy(true);
    try {
      await api.delete(`/apps/${app.id}/growth/google-ads`);
      await load();
      toast.success('Google Ads disconnected; ads remain off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not disconnect Google Ads');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-xl border border-primary/20 p-5 space-y-5" data-testid="google-ads-control-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold">Google Ads control center</h3>
          <p className="text-xs text-muted-foreground mt-1">Paid search for {app.name}, controlled separately from organic Google Search and LinkedIn.</p>
        </div>
        {!status?.connected ? (
          <button type="button" onClick={connect} disabled={busy || !status?.configured} className="px-3 py-2 rounded-lg bg-primary text-white text-xs disabled:opacity-50" data-testid="connect-google-ads">
            Connect Google Ads
          </button>
        ) : (
          <div className="flex gap-2">
            <button type="button" onClick={sync} disabled={busy || !selected} className="px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs disabled:opacity-50">Update results</button>
            <button type="button" onClick={disconnect} disabled={busy} className="px-3 py-2 rounded-lg border border-red-500/20 text-red-300 text-xs disabled:opacity-50">Disconnect</button>
          </div>
        )}
      </div>

      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-xs text-emerald-200" data-testid="google-ads-spend-lock">
        <strong>Spend lock: ON.</strong> Mayr Ops can read results and prepare approval records, but it cannot activate a live campaign. Saved hard budget: €{hardCap.toFixed(2)}/day.
      </div>
      {!status?.configured && <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-200">One-time server setup is still required: Google Ads OAuth credentials and developer token. No ad account is connected yet.</div>}

      {status?.connected && (
        <div className="space-y-3 rounded-lg bg-secondary/20 border border-white/5 p-3">
          <div className="text-sm font-medium">1. Choose the Google Ads account for {app.name}</div>
          <input value={managerId} onChange={(event) => setManagerId(event.target.value)} placeholder="Optional manager account ID (123-456-7890)" className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <div className="flex flex-wrap gap-2">
            {accountOptions.length === 0 ? <span className="text-xs text-muted-foreground">No accessible account was returned. Check access in Google Ads.</span> : accountOptions.map((customerId) => (
              <button key={customerId} type="button" onClick={() => chooseAccount(customerId)} disabled={busy} className={`px-3 py-2 rounded-lg border text-xs font-mono ${selected === customerId ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-secondary/30 text-muted-foreground'}`}>
                {selected === customerId ? '✓ ' : ''}{customerId.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
              </button>
            ))}
          </div>
          {status.last_sync_error && <p className="text-xs text-destructive">{status.last_sync_error}</p>}
          {selected && (
            <div className="grid sm:grid-cols-4 gap-2">
              <div className="rounded-lg bg-secondary/30 p-3"><div className="text-xs text-muted-foreground">Clicks (30 days)</div><div className="text-lg font-bold">{count(metrics.clicks)}</div></div>
              <div className="rounded-lg bg-secondary/30 p-3"><div className="text-xs text-muted-foreground">Impressions</div><div className="text-lg font-bold">{count(metrics.impressions)}</div></div>
              <div className="rounded-lg bg-secondary/30 p-3"><div className="text-xs text-muted-foreground">Ad cost</div><div className="text-lg font-bold">{money(metrics.cost_micros)}</div></div>
              <div className="rounded-lg bg-secondary/30 p-3"><div className="text-xs text-muted-foreground">Conversions</div><div className="text-lg font-bold">{count(metrics.conversions)}</div></div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={createProposal} className="space-y-3 rounded-lg bg-secondary/20 border border-white/5 p-3">
        <div>
          <div className="text-sm font-medium">2. Prepare a Search campaign proposal</div>
          <div className="text-xs text-muted-foreground mt-1">Reviewable plan only. Approval records your decision; it does not publish or activate the campaign.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="Campaign name" className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} placeholder="Landing page URL" className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input type="number" min="1" max={hardCap || undefined} step="0.01" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} placeholder="Daily budget (EUR)" className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <div className="text-xs text-muted-foreground self-center">Cannot exceed the saved €{hardCap.toFixed(2)} daily cap.</div>
          <textarea value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="Search keywords, one per line" rows={4} className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.negative_keywords} onChange={(e) => setForm({ ...form, negative_keywords: e.target.value })} placeholder="Negative keywords, one per line" rows={4} className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.headlines} onChange={(e) => setForm({ ...form, headlines: e.target.value })} placeholder="3–15 headlines, max 30 characters each" rows={4} className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.descriptions} onChange={(e) => setForm({ ...form, descriptions: e.target.value })} placeholder="2–4 descriptions, max 90 characters each" rows={4} className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button type="submit" disabled={busy || hardCap <= 0} className="w-full py-2.5 rounded-lg bg-primary text-white text-sm disabled:opacity-50" data-testid="save-google-ads-proposal">Save proposal for approval</button>
      </form>

      <div className="space-y-2">
        <div className="text-sm font-medium">3. Review and approve</div>
        {proposals.length === 0 ? <div className="text-xs text-muted-foreground">No Google Ads proposals for this app yet.</div> : proposals.map((item) => (
          <div key={item.id} className="rounded-lg bg-secondary/20 border border-white/5 p-3 space-y-2" data-testid={`google-ads-proposal-${item.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><span className="font-medium text-sm">{item.campaign_name}</span><span className="ml-2 text-xs text-muted-foreground">€{(item.daily_budget_cents / 100).toFixed(2)}/day</span></div>
              <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'approved' ? 'bg-emerald-500/10 text-emerald-300' : item.status === 'rejected' ? 'bg-red-500/10 text-red-300' : 'bg-amber-500/10 text-amber-300'}`}>{item.status}</span>
            </div>
            <div className="text-xs font-mono text-primary break-all">{item.destination_url}</div>
            <div className="text-xs text-muted-foreground">{asArray(item.keywords).length} keywords · {asArray(item.negative_keywords).length} exclusions · {asArray(item.headlines).length} headlines</div>
            {item.status === 'draft' && <div className="flex gap-2"><button type="button" onClick={() => decide(item, 'approve')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs">Approve plan</button><button type="button" onClick={() => decide(item, 'reject')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">Reject</button></div>}
            {item.status === 'approved' && <div className="text-xs text-emerald-200">Approved and retained safely in Mayr Ops. Live Google activation is still locked.</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
